import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import {
    bookingStatusOptionsV2,
    discountOptionsV2,
    timeHourOptionsV2,
    timeMinuteIntervalFiveOptionsV2,
} from '../components/settings/Options'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { BookableColorKey, getDefaultOrderColorKey } from '../utils/calendarColors'
import { formatTimeStamp } from '../utils/dates'
import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { Customizable } from './Customizable'
import { Estimate } from './Estimate'
import { Field } from './Field'
import { Gift } from './Gift'
import { Item } from './Item'
import { Line } from './Line'
import { Mappable } from './Mappable'
import { Option } from './Option'
import { Payment } from './Payment'
import { Person } from './Person'
import { Promotion } from './Promotion'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { FKType } from './SQLBuilder'
import { Source } from './Source'
import { Subaccount } from './Subaccount'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'
import { User } from './User'

type OrderConstructorArgs = Omit<ConstructorArgs<() => Order>, 'online'> & {
    is_online: boolean | null
}

export class Order extends Transaction implements Bookable, Customizable, Mappable, Transactable {
    readonly recordType = 'order'

    readonly iBookable = true
    readonly iCustomizable = true
    readonly iMappable = true
    readonly iTransactable = true

    longitude?: number
    latitude?: number
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    countryCode?: string

    fieldValues: RecordDataMap = new RecordDataMap()

    memo?: string
    type?: string
    status?: string
    confirmStatus?: string
    duration?: number
    instructions?: string

    online?: boolean
    allowSurcharge?: boolean

    rate?: number
    ship?: number
    shipTaxable?: boolean
    number?: number
    shipPricing?: string
    order?: string
    parcel?: string
    parcelCount?: number
    carrier?: string
    delivery?: string
    tracker?: string
    trackingCode?: string
    checkout?: string // only set during client checkout (used to avoid double booking)
    pickupCode?: string // only set during completion (auto-generated on backend but never sent to client)

    cancellationType?: string
    cancellationMemo?: string

    markPaidDate?: moment.Moment
    confirmedDate?: moment.Moment
    completedDate?: moment.Moment
    deliveredDate?: moment.Moment

    readonly trackerStatus?: string | undefined
    readonly trackerUrl?: string | undefined
    readonly trackerShipper?: string | undefined
    readonly trackerSignedBy?: string | undefined

    readonly sourceDisplay?: string

    constructor(data?: Partial<OrderConstructorArgs>) {
        super(data)
        if (!data) {
            this.channel = 'sms'
            this.reminder = '48'
            this.fieldValues = new RecordDataMap()
            return
        }

        this.longitude = this.propertyFromNumberArg(data.longitude)
        this.latitude = this.propertyFromNumberArg(data.latitude)
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.countryCode = data.country_code ?? undefined
        this.duration = this.propertyFromIntArg(data.duration)
        this.instructions = data.instructions ?? undefined
        this.online = data.is_online ?? false
        this.allowSurcharge = data.allow_surcharge ?? false

        this.fieldValues = Field.loadFromRecordMap(
            new RecordDataMap(data),
            !data.owner ? undefined : typeof data.owner === 'string' ? data.owner : data.owner.id,
            undefined
        )
        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.ship = this.propertyFromCurrencyArg(data.ship)
        this.shipTaxable = data.ship_taxable ?? false
        this.shipPricing = data.ship_pricing ?? undefined
        this.parcel = data.parcel ?? undefined
        this.parcelCount = this.propertyFromIntArg(data.parcel_count)
        this.carrier = data.carrier ?? undefined
        this.delivery = data.delivery ?? undefined
        this.order = data.order ?? undefined
        this.tracker = data.tracker ?? undefined
        this.trackingCode = data.tracking_code ?? undefined

        this.number = this.propertyFromIntArg(data.number)
        this.memo = data.memo ?? undefined
        this.type = data.type ?? undefined
        this.status = data.status ?? undefined
        this.cancellationType = data.cancellation_type ?? undefined
        this.cancellationMemo = data.cancellation_memo ?? undefined
        this.confirmStatus = data.confirm_status ?? undefined
        this.checkout = data.checkout ?? undefined

        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.confirmedDate = this.propertyFromTimestampArg(data.confirmed_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.deliveredDate = this.propertyFromTimestampArg(data.delivered_date)

        this.trackerStatus = data.tracker_status ?? undefined
        this.trackerUrl = data.tracker_url ?? undefined
        this.trackerShipper = data.tracker_shipper ?? undefined
        this.trackerSignedBy = data.tracker_signed_by ?? undefined

        this.sourceDisplay = data.source_display ?? undefined
    }

    clone(overrides?: Partial<OrderConstructorArgs>): Order {
        return new Order({ ...this.serializeForClone(), is_online: this.online, ...overrides })
    }

    static channelStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: "Don't notify" }))
        strings.push(new Option(undefined, { id: 'sms', label: 'Text message' }))
        strings.push(new Option(undefined, { id: 'combo', label: 'Text and email' }))
        strings.push(new Option(undefined, { id: 'email', label: 'Email' }))
        return strings
    }

    static durationStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '0', label: 'No duration' }))
        strings.push(new Option(undefined, { id: '10', label: '10 minutes' }))
        strings.push(new Option(undefined, { id: '15', label: '15 minutes' }))
        strings.push(new Option(undefined, { id: '20', label: '20 minutes' }))
        strings.push(new Option(undefined, { id: '25', label: '25 minutes' }))
        strings.push(new Option(undefined, { id: '30', label: '30 minutes' }))
        strings.push(new Option(undefined, { id: '35', label: '35 minutes' }))
        strings.push(new Option(undefined, { id: '40', label: '40 minutes' }))
        strings.push(new Option(undefined, { id: '45', label: '45 minutes' }))
        strings.push(new Option(undefined, { id: '50', label: '50 minutes' }))
        strings.push(new Option(undefined, { id: '55', label: '55 minutes' }))
        strings.push(new Option(undefined, { id: '60', label: '1 hour' }))
        for (let i = 65; i <= 720; i += 5) {
            const id = i.toString()
            const hourString = i / 60 < 2 ? '1 hour' : i / 60 + ' hours'
            if (i % 60 > 0) {
                const minuteString = (i % 60) + ' minutes'
                strings.push(
                    new Option(undefined, { id: id, label: hourString + ', ' + minuteString })
                )
            }
            else strings.push(new Option(undefined, { id: id, label: hourString }))
        }
        return strings
    }

    static reminderStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: "Don't remind" }))
        strings.push(new Option(undefined, { id: '2', label: '2 hours' }))
        strings.push(new Option(undefined, { id: '4', label: '4 hours' }))
        strings.push(new Option(undefined, { id: '6', label: '6 hours' }))
        strings.push(new Option(undefined, { id: '8', label: '8 hours' }))
        strings.push(new Option(undefined, { id: '12', label: '12 hours' }))
        strings.push(new Option(undefined, { id: '24', label: '24 hours' }))
        strings.push(new Option(undefined, { id: '36', label: '36 hours' }))
        strings.push(new Option(undefined, { id: '48', label: '2 days' }))
        strings.push(new Option(undefined, { id: '72', label: '3 days' }))
        return strings
    }

    static cancellationTypeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'late_cancellation', label: 'Late cancellation' }))
        strings.push(new Option(undefined, { id: 'no_show', label: 'No show' }))
        strings.push(new Option(undefined, { id: 'courtesy', label: 'Courtesy cancellation' }))
        strings.push(new Option(undefined, { id: 'reschedule', label: 'Rescheduled' }))
        strings.push(new Option(undefined, { id: 'defective', label: 'Damaged' }))
        strings.push(new Option(undefined, { id: 'not_received', label: 'Never received' }))
        strings.push(new Option(undefined, { id: 'wrong_product', label: 'Wrong product' }))
        return strings
    }

    static typeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'pickup', label: 'Pickup' }))
        strings.push(new Option(undefined, { id: 'ship', label: 'Shipping' }))
        return strings
    }

    set endDate(endDate: moment.Moment | undefined) {
        if (endDate && this.date) {
            this.duration = Math.floor(
                moment.duration(endDate.valueOf() - this.date.valueOf()).asMinutes()
            )
        }
    }

    get lines(): Line[] {
        return super.lines
    }

    set lines(lines: Line[]) {
        super.lines = lines
        for (const line of lines || []) line.trandoc = this
    }

    get allowSalesTax(): boolean {
        return !!this.nexus
    }

    get forPickup(): boolean {
        return 'pickup' === this.type
    }

    get endDate(): moment.Moment | undefined {
        return this.date?.clone().add(this.duration ?? 0, 'minutes')
    }

    get products(): Item[] {
        const items = []
        for (const line of this.lines) {
            const item = line.item
            if (item && 'product' === item.type) {
                items.push(item)
            }
        }
        return items
    }

    get leadtime(): string | undefined {
        let leadtime = undefined
        for (const item of this.products) {
            if (!!item.leadtime && (!leadtime || parseInt(item.leadtime) > parseInt(leadtime))) {
                leadtime = item.leadtime
            }
        }
        return leadtime
    }

    get leadtimeString(): string | undefined {
        let instance = undefined
        for (const item of this.products) {
            if (
                !!item.leadtime &&
                (!instance || parseInt(item.leadtime) > parseInt(instance.leadtime ?? '0'))
            ) {
                instance = item
            }
        }
        return instance?.leadtimeString
    }

    get item(): Item | undefined {
        const items = this.products
        return items.length > 0 ? items[0] : undefined
    }

    async fields(): Promise<Field[]> {
        return await RecordFactory.getInstance().loadOrderFields(
            this.owner!.id!,
            false,
            PSApplication.role() === User.PERM_ROLE_CLIENT
        )
    }

    /* read-only name, address fields */
    getLocation(): string | undefined {
        return Person.locationString(
            this.line1,
            this.line2,
            this.city,
            this.state,
            this.postalCode,
            this.location
        )
    }

    get items(): Item[] {
        const items = []
        for (const line of this.lines) {
            const item = line.item
            if (item) {
                items.push(item)
            }
        }
        return items
    }

    isLate(hours: number): boolean {
        const deadline = this.createdDate!.clone().add(hours, 'hours')
        return !!this.confirmedDate && moment().isAfter(deadline)
    }

    get sourceString(): string | undefined {
        return !!this.id ? this.sourceDisplay : undefined
    }

    get statusString(): string | undefined {
        if (this.id) {
            if (this.deliveredDate) return `Delivered ${this.deliveredDate.format('MMM Do YYYY')}`
            if (this.completedDate && this.forPickup)
                return `Picked up ${this.completedDate.format('MMM Do YYYY')}`
            if (this.completedDate) return `Shipped ${this.completedDate.format('MMM Do YYYY')}`
            if (this.markPaidDate && this.paymeth)
                return `Marked paid ${this.markPaidDate.format('MMM Do YYYY')} - ${this.paymeth}`
            if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM Do YYYY')}`
            if (this.paidDate) return `Paid ${this.paidDate.format('MMM Do YYYY')}`
            if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM Do YYYY')}`
            if (this.confirmedDate && this.source)
                return `Confirmed ${this.confirmedDate.format('MMM Do YYYY')} - card`
            if (this.confirmedDate) return `Confirmed ${this.confirmedDate.format('MMM Do YYYY')}`
            if ('confirmed' === this.status) return 'Confirmed'
            if ('sentQuote' === this.status && 'confirmPayment' === this.confirmStatus)
                return 'Requires credit card'
            if ('sentQuote' === this.status) return 'Requires confirmation'
            if (this.online) return `Requested online ${this.createdDate!.format('MMM Do YYYY')}`
            if (this.id) return `Requested ${this.createdDate!.format('MMM Do YYYY')}`
            return 'Requires approval'
        }
        else return this.editStatusString
    }

    get editStatusString(): string | undefined {
        if ('confirmed' === this.status) return 'Already confirmed'
        if ('sentQuote' === this.status && 'confirmPayment' === this.confirmStatus)
            return 'Requires credit card'
        if ('sentQuote' === this.status) return 'Requires confirmation'
        if (!this.id && ['package', 'subscription', 'gift'].indexOf(this.item!.type!) !== -1)
            return this.total && this.total > 0 ? 'Pending payment' : 'Pending confirmation'
        if (
            'sentRequest' === this.status &&
            'confirmed' === this.vendor!.bookingStatus &&
            PSApplication.role() === User.PERM_ROLE_CLIENT
        )
            return 'Confirm order'
        return 'Place order'
    }

    toString(): string | undefined {
        if (this.location) return this.location
        return this.title
    }

    get title(): string | undefined {
        const person = this.person
        if (person && this.item) return `${person.name} - ${this.item.name}`
        else if (person) return `${person.name}`
        else if (this.item) return `${this.item.name}`
        return `Product order - ${this.statusString} (PocketSuite)`
    }

    get shortTitle(): string | undefined {
        const person = this.person
        if (person) return person.name
        return `Product order - ${this.statusString} (PocketSuite)`
    }

    get color(): BookableColorKey {
        return getDefaultOrderColorKey()
    }

    get allowNotify(): boolean {
        return !!this.channel
    }

    get scheduleState(): ScheduleState[] {
        if (this.completedDate) return [ScheduleState.Complete]
        const states = []
        if (!this.confirmedDate) {
            states.push(ScheduleState.Confirmable)
            states.push(ScheduleState.Cancelable)
        }
        else if (!this.paidDate) {
            states.push(ScheduleState.Chargeable)
            states.push(ScheduleState.Cancelable)
        }
        else {
            states.push(ScheduleState.Completable)
        }
        return states
    }

    // needs an equivalent for LatLng in Java
    get coordinate(): [number, number] | undefined {
        if (this.latitude && this.longitude) return [this.latitude, this.longitude]
        return undefined
    }

    get total(): number | undefined {
        return this.rate
    }

    get amount(): number | undefined {
        if (this.discount && this.discount > 0)
            return (this.total ?? 0) - (this.discountAmount ?? 0)
        return this.total
    }

    get shipAmount(): number {
        if (this.ship === 0) return 0

        let price = 0
        const pricing = this.shipPricing
        if ('weight' === pricing) {
            const totalWeight = this.weight
            if (totalWeight) price = (this.ship ?? 0) * totalWeight
        }
        else if ('unit' === pricing) {
            const totalUnits = this.units
            if (totalUnits) price = (this.ship ?? 0) * totalUnits
        }
        else if ('percent' === pricing) {
            price = currencyFloat(((this.ship ?? 0) * (this.total ?? 0)) / 100)
        }
        else price = this.ship ?? 0
        return price
    }

    get surchargeAmount(): number {
        if (this.allowSurcharge)
            return (
                ((this.amount ?? 0) + (this.shipAmount ?? 0) + (this.taxAmount ?? 0)) *
                Payment.PAYMENT_SURCHARGE_RATE
            )
        return 0
    }

    get chargeAmount(): number {
        return (
            (this.amount ?? 0) +
            (this.shipAmount ?? 0) +
            (this.taxAmount ?? 0) +
            this.surchargeAmount
        )
    }

    get askingAmount(): number {
        return (this.amount ?? 0) + (this.shipAmount ?? 0) + (this.taxAmount ?? 0)
    }

    get finalChargeAmount(): number {
        return this.chargeAmount
    }

    get finalAskingAmount(): number {
        return this.askingAmount
    }

    get taxableAmount(): number {
        const taxcode = this.taxcode
        if (this.lines.length > 0 && taxcode) {
            let total = 0
            for (const line of this.lines) {
                if (line.amount && line.amount > 0 && line.taxable) total += line.amount
            }
            if (this.shipTaxable && this.shipAmount && this.shipAmount > 0) total += this.shipAmount
            return total
        }
        let total = taxcode ? (this.amount ?? 0) : 0
        if (this.shipTaxable && this.shipAmount && this.shipAmount > 0) total += this.shipAmount
        return total
    }

    get taxAmount(): number {
        const taxableAmount = this.taxableAmount
        if (taxableAmount > 0) {
            if (this.tax && this.tax > 0) return currencyFloat((this.tax * taxableAmount) / 100)
            else if (
                this.vendor &&
                this.vendor.taxcode &&
                this.vendor.taxcode.rate &&
                this.vendor.taxcode.rate > 0
            )
                return currencyFloat((this.vendor.taxcode.rate * taxableAmount) / 100)
        }
        return 0
    }

    get finalTaxAmount(): number {
        return this.taxAmount
    }

    get discountAmount(): number {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            const total = this.discountableTotal ?? 0
            if ('percent' === this.discountType)
                discount = currencyFloat((this.discount * total) / 100)
            else if (discount > total) discount = total
            return discount
        }
        return 0
    }

    get discountableTotal(): number | undefined {
        if (this.lines.length > 0) {
            let total = 0
            for (const line of this.lines) {
                if (line.discountable && line.total) total += line.total
            }
            return total
        }
        return this.total
    }

    get paidAmount(): number {
        if (this.markPaidDate) return 0
        let amount = this.amount ?? 0
        if (this.taxAmount && this.taxAmount > 0) amount += this.taxAmount
        if (this.shipAmount && this.shipAmount > 0) amount += this.shipAmount
        return amount
    }

    get owedAmount(): number | undefined {
        if (this.paidDate) return 0
        return this.amount
    }

    setAddressFromUser(user: User | undefined) {
        if (user) {
            this.line1 = user.line1
            this.line2 = user.line2
            this.city = user.city
            this.state = user.state
            this.postalCode = user.postalCode
        }
        else {
            this.line1 = undefined
            this.line2 = undefined
            this.city = undefined
            this.state = undefined
            this.postalCode = undefined
        }
    }

    setAddressFromSubaccount(subaccount: Subaccount | undefined) {
        if (!this.forPickup && subaccount && subaccount.line1) {
            this.line1 = subaccount.line1
            this.line2 = subaccount.line2
            this.city = subaccount.city
            this.state = subaccount.state
            this.postalCode = subaccount.postalCode
        }
    }

    static parcelStrings(carrier: string | undefined): Option[] {
        const strings: Option[] = []
        if ('ups' === carrier) {
            strings.push(
                new Option(undefined, {
                    id: 'small',
                    label: 'Small',
                    subtitle: '13.0 x 11.0 x 2.0 inches (30 lbs)',
                })
            )
            strings.push(
                new Option(undefined, {
                    id: 'medium',
                    label: 'Medium',
                    subtitle: '16.5 x 13.25 x 10.75 inches (22 lbs)',
                })
            )
            strings.push(
                new Option(undefined, {
                    id: 'large',
                    label: 'Large',
                    subtitle: '19.75 x 17.75 x 13.25 inches (55 lbs)',
                })
            )
        }
        else if ('fedex' === carrier) {
            strings.push(
                new Option(undefined, {
                    id: 'small',
                    label: 'Small',
                    subtitle: '8.75 x 11.25 x 2.63 inches (20 lbs)',
                })
            )
            strings.push(
                new Option(undefined, {
                    id: 'medium',
                    label: 'Medium',
                    subtitle: '15.81 x 12.94 x 10.19 inches (22 lbs)',
                })
            )
            strings.push(
                new Option(undefined, {
                    id: 'large',
                    label: 'Large',
                    subtitle: '21.56 x 16.56 x 13.19 inches (55 lbs)',
                })
            )
        }
        else if ('usps' === carrier) {
            strings.push(
                new Option(undefined, {
                    id: 'small',
                    label: 'Small',
                    subtitle: '8.7 x 5.4 x 1.75 inches (70 lbs)',
                })
            )
            strings.push(
                new Option(undefined, {
                    id: 'medium',
                    label: 'Medium',
                    subtitle: '14.0 x 12.0 x 3.5 inches (70 lbs)',
                })
            )
            strings.push(
                new Option(undefined, {
                    id: 'large',
                    label: 'Large',
                    subtitle: '12.0 x 12.25 x 6.0 inches (70 lbs)',
                })
            )
        }
        else {
            strings.push(new Option(undefined, { id: 'small', label: 'Small' }))
            strings.push(new Option(undefined, { id: 'medium', label: 'Medium' }))
            strings.push(new Option(undefined, { id: 'large', label: 'Large' }))
        }
        return strings
    }

    static carrierStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'ups', label: 'UPS' }))
        strings.push(new Option(undefined, { id: 'fedex', label: 'FedEx' }))
        strings.push(new Option(undefined, { id: 'usps', label: 'US Post Office' }))
        return strings
    }

    static deliveryStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '1d', label: 'Next day delivery' }))
        strings.push(new Option(undefined, { id: '2d', label: '2 day delivery' }))
        strings.push(new Option(undefined, { id: 'ground', label: '3 to 5 day delivery' }))
        return strings
    }

    static shipDiscountStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '3', label: '3%' }))
        strings.push(new Option(undefined, { id: '4', label: '4%' }))
        strings.push(new Option(undefined, { id: '5', label: '5%' }))
        strings.push(new Option(undefined, { id: '6', label: '6%' }))
        strings.push(new Option(undefined, { id: '7', label: '7%' }))
        strings.push(new Option(undefined, { id: '8', label: '8%' }))
        strings.push(new Option(undefined, { id: '9', label: '9%' }))
        strings.push(new Option(undefined, { id: '10', label: '10%' }))
        strings.push(new Option(undefined, { id: '11', label: '11%' }))
        strings.push(new Option(undefined, { id: '12', label: '12%' }))
        strings.push(new Option(undefined, { id: '13', label: '13%' }))
        strings.push(new Option(undefined, { id: '14', label: '14%' }))
        strings.push(new Option(undefined, { id: '15', label: '15%' }))
        strings.push(new Option(undefined, { id: '20', label: '20%' }))
        strings.push(new Option(undefined, { id: '25', label: '25%' }))
        return strings
    }

    static pricingStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'carrier', label: 'Per carrier' }))
        strings.push(new Option(undefined, { id: 'flat', label: 'Flat rate' }))
        strings.push(new Option(undefined, { id: 'weight', label: 'Per pound' }))
        strings.push(new Option(undefined, { id: 'unit', label: 'Per unit' }))
        strings.push(new Option(undefined, { id: 'percent', label: 'Percent of total' }))
        return strings
    }

    static shipTypeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'pickup', label: 'Pickup only' }))
        strings.push(new Option(undefined, { id: 'both', label: 'Pickup or shipping' }))
        return strings
    }

    static parcelString(parcel: string, carrier: string): string {
        let parcelString: string | undefined = undefined
        const l = Order.parcelStrings(carrier)
        for (const opt of l) if (opt.id === parcel) parcelString = opt.label
        return parcelString || ''
    }

    get carrierString(): string | undefined {
        const carrier = this.carrier
        const l = Order.carrierStrings()
        for (const opt of l) if (opt.id === carrier) return opt.label
        return undefined
    }

    get deliveryString(): string | undefined {
        const delivery = this.delivery
        const l = Order.deliveryStrings()
        for (const opt of l) if (opt.id === delivery) return opt.label
        return undefined
    }

    get pricingString(): string | undefined {
        const shipPricing = this.shipPricing
        const l = Order.pricingStrings()
        for (const opt of l) if (opt.id === shipPricing) return opt.label
        return undefined
    }

    get typeString(): string | undefined {
        const type = this.type
        const l = Order.typeStrings()
        for (const opt of l) if (opt.id === type) return opt.label
        return undefined
    }

    allItemString(limit?: number): string {
        const strings: string[] = []
        for (const line of this.lines)
            if (!limit || strings.length < limit) strings.push(line.itemDisplay ?? '')

        if (this.lines.length > strings.length) strings.push('...')
        return strings.join(', ')
    }

    get statusOption(): Option | undefined {
        let status =
            this.confirmedDate || this.status === 'confirmed'
                ? ''
                : this.confirmStatus === 'confirmPayment'
                  ? 'confirmPayment'
                  : 'confirmOnly'
        return Option.findOption(status, bookingStatusOptionsV2)
    }

    get parcelOption(): Option | undefined {
        if (!!this.parcel && !!this.carrier)
            return Option.findOption(this.parcel, Order.parcelStrings(this.carrier))
        return undefined
    }

    get carrierOption(): Option | undefined {
        if (!!this.carrier) return Option.findOption(this.carrier, Order.carrierStrings())
        return undefined
    }

    get deliveryOption(): Option | undefined {
        if (!!this.delivery) return Option.findOption(this.delivery, Order.deliveryStrings())
        return undefined
    }

    get pricingOption(): Option | undefined {
        if (!!this.shipPricing) return Option.findOption(this.shipPricing, Order.pricingStrings())
        return undefined
    }

    get percentShippingOption(): Option | undefined {
        if (this.ship !== undefined)
            return Option.findOption(this.ship.toString(), Order.shipDiscountStrings())
        return undefined
    }

    get typeOption(): Option | undefined {
        if (!!this.type) return Option.findOption(this.type, Order.typeStrings())
        return undefined
    }

    get giftStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'None' }))
        if (this.client && this.owner) {
            //const gifts: Gift[] = Gift.loadAllPaidGifts(this.client, this.owner) record factory call here
            const gifts: Gift[] = []
            for (let i = 0; i < gifts.length; i++) {
                const gift = gifts[i]
                strings.push(
                    new Option(undefined, { id: gift.id, label: `${gift.name} - ${gift.balance}` })
                )
            }
        }
        return strings
    }

    get giftOption(): Option | undefined {
        let id = this.gift?.id
        if (id !== undefined) return Option.findOption(id, this.giftStrings)
        return undefined
    }

    get durationOption(): Option | undefined {
        return Option.findOption(this.duration?.toString(), Order.durationStrings())
    }

    get hourOption(): Option | undefined {
        if (this.date) {
            const hour = this.date.format('HH')
            return Option.findOption(hour, timeHourOptionsV2)
        }
        return undefined
    }

    get minuteOption(): Option | undefined {
        if (this.date) {
            const minute = this.date.format('mm')
            const opt = Option.findOption(minute, timeMinuteIntervalFiveOptionsV2)
            return opt
        }
        return undefined
    }

    nexusOption(nexus_strings: Option[]): Option | undefined {
        if (this.nexus !== undefined) return Option.findOption(this.nexus, nexus_strings)
        return undefined
    }

    get discountOption(): Option | undefined {
        if (this.discountType === 'percent' && this.discount !== undefined)
            return Option.findOption(this.discount.toString(), discountOptionsV2)
        return undefined
    }

    get channelOption(): Option | undefined {
        if (this.channel !== undefined)
            return Option.findOption(this.channel, Order.channelStrings())
        return undefined
    }

    get reminderOption(): Option | undefined {
        if (this.reminder !== undefined)
            return Option.findOption(this.reminder, Order.reminderStrings())
        return undefined
    }

    allItemStringAndCount(limit?: number): string {
        const strings: string[] = []
        for (const line of this.lines) {
            if (!limit || strings.length < limit) {
                const item_display = line.item?.name
                const quantity = line.quantity
                if (item_display) strings.push(`${item_display} x${quantity}`)
            }
        }
        if (this.lines.length > strings.length) strings.push('...')
        return strings.join(', ')
    }

    // Bookable interface methods
    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return false
    }

    get hasSchedule(): boolean {
        return false
    }

    get isRecurring(): boolean {
        return false
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    get scheduleStates(): ScheduleState[] {
        return [ScheduleState.Complete]
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.date) this.date = moment(data.date)
        if (data.promotion) this.promotion = BaseRecord.fromSerializer(data.promotion, Promotion)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data.fieldValues) this.fieldValues = new RecordDataMap(data.fieldValues)
        if (data.estimate) this.estimate = BaseRecord.fromSerializer(data.estimate, Estimate)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.confirmedDate) this.confirmedDate = moment(data.confirmedDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.deliveredDate) this.deliveredDate = moment(data.deliveredDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        promotions: ['promotion'],
        sources: ['source'],
        gifts: ['gift'],
        estimates: ['estimate'],
    })

    static dataType(): DataType {
        return 'orders'
    }

    jsonObject(): any {
        const dict: any = super.jsonObject()
        dict['type'] = this.type
        dict['date'] = formatTimeStamp(this.date)
        dict['duration'] = this.duration ?? ''
        dict['rate'] = this.rate !== undefined ? this.rate : ''
        dict['estimate'] = this.estimate?.id ?? ''
        dict['discount'] = this.discount && this.discount > 0 ? this.discount : ''
        dict['discount_type'] =
            this.discount && this.discount > 0 && this.discountType ? this.discountType : ''
        dict['allow_surcharge'] = this.allowSurcharge
        dict['status'] = this.status ?? ''
        dict['cancellation_type'] = this.cancellationType ?? ''
        dict['cancellation_memo'] = this.cancellationMemo ?? ''
        dict['checkout'] = this.checkout ?? ''
        dict['ship'] = this.ship !== undefined ? this.ship : ''
        dict['ship_taxable'] = this.shipTaxable
        dict['parcel'] = this.parcel ?? ''
        dict['parcel_count'] = this.parcelCount !== undefined ? this.parcelCount : ''
        dict['carrier'] = this.carrier ?? ''
        dict['delivery'] = this.delivery ?? ''
        dict['order'] = this.order ?? ''
        dict['tracking_code'] = this.trackingCode ?? ''
        dict['ship_pricing'] = this.shipPricing ?? ''
        dict['confirm_status'] = this.confirmStatus ?? ''
        dict['gift'] = this.gift?.id ?? ''
        dict['promotion'] = this.promotion?.id ?? ''
        dict['field_values'] =
            this.fieldValues !== undefined
                ? Field.serializeFieldData(this.fieldValues, this.vendor?.id, undefined)
                : ''
        dict['line1'] = this.line1 ?? ''
        dict['line2'] = this.line2 ?? ''
        dict['city'] = this.city ?? ''
        dict['state'] = this.state ?? ''
        dict['postal_code'] = this.postalCode ?? ''
        dict['country_code'] = this.countryCode ?? ''
        dict['memo'] = this.memo ?? ''
        dict['instructions'] = this.instructions ?? ''
        if (this.source) dict['source'] = this.source.id
        const jLines: any[] = []
        for (const line of this.lines) jLines.push(line.jsonObject())
        dict['lines'] = jLines
        return dict
    }

    get promotion(): Promotion | undefined {
        return this.getForeignRecord('promotion', Promotion)
    }
    set promotion(promotion: Promotion | undefined) {
        this.setForeignRecord('promotion', promotion)
    }
    get estimate(): Estimate | undefined {
        return this.getForeignRecord('estimate', Estimate)
    }
    set estimate(estimate: Estimate | undefined) {
        this.setForeignRecord('estimate', estimate)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
